import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Calendar, Clock3, Download, ExternalLink, Eye, FileText, ImageIcon, KeyRound, Loader2, LogIn, Menu, ShieldCheck, Stethoscope, UserRound, X } from 'lucide-react';
import { apiUrl } from '@/lib/api';
import type { ExameImagem } from '@/types/api';

export const Route = createFileRoute('/pep-vida/resultado')({
  component: ResultadoPortal,
});

interface PortalPaciente {
  protocolo: number;
  cdPaciente: number;
  nome?: string;
  nascimento?: string | null;
  sexo?: string | null;
}

interface LoginResponse {
  expiresIn: number;
  paciente: PortalPaciente;
}

interface ExamesResponse {
  paciente: PortalPaciente;
  exames: PortalExame[];
}

interface PortalExame extends ExameImagem {
  isCheckup?: boolean;
  aviso?: string | null;
  cdModalidade?: number | null;
  isCapturaPdf?: boolean;
  imagemPdfs?: number[];
}

export function ResultadoPortal() {
  const initial = useMemo(() => getInitialCredentials(), []);
  const [protocolo, setProtocolo] = useState(initial.protocolo);
  const [cdPaciente, setCdPaciente] = useState(initial.cdPaciente);
  const [nascimento, setNascimento] = useState('');
  const [paciente, setPaciente] = useState<PortalPaciente | null>(null);
  const [exames, setExames] = useState<PortalExame[]>([]);
  const [selected, setSelected] = useState<{ exame: PortalExame; tipo: 'laudo' | 'imagens' | 'dicom' } | null>(null);
  const [selectedCheckupYear, setSelectedCheckupYear] = useState('todos');
  const [selectedOutrosYear, setSelectedOutrosYear] = useState('todos');
  const [openCheckupMenu, setOpenCheckupMenu] = useState(false);
  const [openOutrosMenu, setOpenOutrosMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState<'all' | 'checkup' | 'outros'>('all');
  const [checkupSection, setCheckupSection] = useState<'relatorios' | 'receitas' | 'plano' | 'exames' | 'encaminhamentos'>('relatorios');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dicomFrameUrl, setDicomFrameUrl] = useState('');
  const [dicomLoading, setDicomLoading] = useState(false);
  const [dicomError, setDicomError] = useState('');
  const [interestSent, setInterestSent] = useState(false);
  const [interestLoading, setInterestLoading] = useState(false);

  useEffect(() => {
    document.title = 'Portal de Resultados Vida';
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem('vidaResultadoSession');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved) as { paciente: PortalPaciente };
      if (parsed.paciente) {
        setPaciente(parsed.paciente);
        portalFetch<ExamesResponse>('/exames')
          .then((lista) => setExames(lista.exames))
          .catch(() => sessionStorage.removeItem('vidaResultadoSession'));
      }
    } catch {
      sessionStorage.removeItem('vidaResultadoSession');
    }
  }, []);

  useEffect(() => {
    if (selected?.tipo !== 'dicom') {
      setDicomFrameUrl('');
      setDicomLoading(false);
      setDicomError('');
      return;
    }

    let active = true;
    setDicomFrameUrl('');
    setDicomError('');
    setDicomLoading(true);
    portalFetch<{ url: string }>(`/${selected.exame.cdExame}/dicom-web`)
      .then((result) => {
        if (!active) return;
        setDicomFrameUrl(result.url);
      })
      .catch(() => {
        if (!active) return;
        setDicomError('Não foi possível carregar o viewer dentro da página.');
      })
      .finally(() => {
        if (active) setDicomLoading(false);
      });

    const fallback = window.setTimeout(() => {
      if (active && !dicomFrameUrl) setDicomError('Se continuar carregando, abra em nova aba.');
    }, 8000);

    return () => {
      active = false;
      window.clearTimeout(fallback);
    };
  }, [selected]);

  async function entrar() {
    setLoading(true);
    setError('');
    setSelected(null);
    try {
      const login = await portalFetch<LoginResponse>('/login', {
        method: 'POST',
        body: JSON.stringify({
          protocolo: Number(protocolo),
          cd_paciente: Number(cdPaciente),
          nascimento,
        }),
      });
      const lista = await portalFetch<ExamesResponse>('/exames');
      setPaciente(login.paciente);
      setExames(lista.exames);
      sessionStorage.setItem(
        'vidaResultadoSession',
        JSON.stringify({ paciente: login.paciente }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível acessar seus resultados.');
    } finally {
      setLoading(false);
    }
  }

  async function sair() {
    await portalFetch('/logout', { method: 'POST' }).catch(() => null);
    sessionStorage.removeItem('vidaResultadoSession');
    setPaciente(null);
    setExames([]);
    setSelected(null);
    setError('');
    setSelectedCheckupYear('todos');
    setSelectedOutrosYear('todos');
    setActiveView('all');
    setCheckupSection('relatorios');
  }

  async function demonstrarInteresseCheckup() {
    if (interestLoading || interestSent) return;
    setInterestLoading(true);
    try {
      await portalFetch('/checkup-interest', { method: 'POST' });
      setInterestSent(true);
    } finally {
      setInterestLoading(false);
    }
  }

  const pdfUrl =
    selected?.tipo === 'laudo'
      ? `${apiUrl}/api/portal-resultados/${selected.exame.cdExame}/laudo.pdf`
      : '';
  const dicomWebUrl =
    selected?.tipo === 'dicom'
      ? `${apiUrl}/api/portal-resultados/${selected.exame.cdExame}/dicom-web/open`
      : '';

  if (!paciente) {
    return (
      <main className="grid min-h-screen bg-[#f4f7f9] text-slate-900 lg:grid-cols-[1.05fr_.95fr]">
        <section className="relative hidden overflow-hidden bg-[linear-gradient(135deg,#075f59_0%,#0b8178_52%,#174b67_100%)] p-16 text-white lg:flex lg:flex-col lg:justify-between">
          <div />
          <div className="relative z-10 max-w-2xl">
            <p className="text-xs font-extrabold uppercase tracking-[.18em] text-teal-100">Sua saúde em um só lugar</p>
            <h1 className="mt-4 text-5xl font-black leading-tight tracking-tight xl:text-7xl">
              Resultados com segurança, clareza e facilidade.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-white/80">
              Acesse seus laudos assinados, visualize imagens dos exames e acompanhe seus resultados em uma experiência simples.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 text-sm">
              <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">Acesso protegido</span>
              <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">Laudos digitais</span>
              <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">Imagens dos exames</span>
            </div>
          </div>
          <div className="relative z-10 text-sm text-white/60">Dados protegidos conforme a LGPD.</div>
          <div className="absolute -bottom-44 -right-36 h-[460px] w-[460px] rounded-full border border-white/20 shadow-[0_0_0_70px_rgba(255,255,255,.04),0_0_0_140px_rgba(255,255,255,.03)]" />
        </section>

        <section className="grid place-items-center p-6 lg:p-12">
          <div className="w-full max-w-[500px]">
            <div className="mb-6 flex justify-center">
              <img src="/pep-vida/brand/logo-vida.png" alt="Vida Diagnóstico e Saúde" className="h-20 w-auto" />
            </div>
          <div className="rounded-[28px] border border-slate-200 bg-white p-7 shadow-[0_16px_44px_rgba(18,63,78,.12)] sm:p-10">
            <p className="text-xs font-extrabold uppercase tracking-[.16em] text-teal-700">Portal do paciente</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight">Acesse seus resultados</h2>
            <p className="mt-2 text-slate-500">Informe os dados entregues no atendimento.</p>

            <div className="mt-7 space-y-4">
              <Field label="Protocolo" icon={<UserRound className="h-4 w-4" />}>
                <input className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 pl-11 pr-4 outline-none transition focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10" inputMode="numeric" value={protocolo} onChange={(event) => setProtocolo(event.target.value)} />
              </Field>
              <Field label="Senha" icon={<KeyRound className="h-4 w-4" />}>
                <input className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 pl-11 pr-4 outline-none transition focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10" inputMode="numeric" type="password" value={cdPaciente} onChange={(event) => setCdPaciente(event.target.value)} />
              </Field>
              <Field label="Data de nascimento" icon={<Calendar className="h-4 w-4" />}>
                <input className="w-full min-w-0 max-w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 py-3.5 pl-11 pr-4 outline-none transition focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10" type="date" value={nascimento} onChange={(event) => setNascimento(event.target.value)} />
              </Field>

              {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

              <button
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-700 to-teal-500 px-5 py-4 font-extrabold text-white shadow-lg shadow-teal-700/20 disabled:opacity-60"
                disabled={loading || !protocolo || !cdPaciente || !nascimento}
                onClick={entrar}
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
                Entrar no portal
              </button>
            </div>

            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-500">
              <strong className="text-slate-700">Precisa de ajuda?</strong>
              <br />
              Protocolo e senha estão no comprovante entregue no atendimento.
              <a
                className="mt-4 flex items-center gap-3 rounded-xl border border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50 px-4 py-3 font-bold text-teal-800 shadow-sm transition hover:border-teal-300 hover:shadow-md"
                href="https://agendamento.vidavg.com.br/usuario/medico"
                target="_blank"
                rel="noreferrer"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-teal-700 text-white shadow-sm">
                  <Stethoscope className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="flex-1">
                  <span className="block text-xs font-extrabold uppercase tracking-wide text-teal-700">Acesso médico</span>
                  <span className="block">Portal Médico Solicitante</span>
                  <span className="block text-xs font-medium text-teal-700">Clique aqui para acessar</span>
                </span>
                <ExternalLink className="h-4 w-4 shrink-0 text-teal-700" aria-hidden="true" />
              </a>
            </div>
          </div>
          </div>
        </section>
      </main>
    );
  }

  const checkupExames = sortByRecentDate(exames.filter((exame) => exame.isCheckup && !isRetornoCheckup(exame)));
  const outrosExames = sortByRecentDate(exames.filter((exame) => !exame.isCheckup));
  const checkupYears = getYears(checkupExames);
  const outrosYears = getYears(outrosExames);
  const filteredCheckupExames =
    selectedCheckupYear === 'todos'
      ? checkupExames
      : checkupExames.filter((exame) => getYear(exame.data) === selectedCheckupYear);
  const checkupRelatorios = filteredCheckupExames.filter((exame) => isRelatorio(exame));
  const checkupReceitas = filteredCheckupExames.filter((exame) => isReceita(exame));
  const checkupPlano = filteredCheckupExames.filter((exame) => isPlanoAlimentar(exame));
  const checkupEncaminhamentos = filteredCheckupExames.filter((exame) => isEncaminhamento(exame));
  const checkupExamesLista = filteredCheckupExames.filter((exame) => isExame(exame));
  const visibleCheckupExames =
    checkupSection === 'relatorios'
      ? checkupRelatorios
      : checkupSection === 'receitas'
        ? checkupReceitas
        : checkupSection === 'plano'
          ? checkupPlano
          : checkupSection === 'encaminhamentos'
            ? checkupEncaminhamentos
          : checkupExamesLista;
  const filteredOutrosExames =
    selectedOutrosYear === 'todos'
      ? outrosExames
      : outrosExames.filter((exame) => getYear(exame.data) === selectedOutrosYear);

  return (
    <main className="min-h-screen bg-[#f4f7f9] text-slate-900">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="flex h-[78px] items-center justify-between px-5 lg:px-8">
          <button
            className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 text-slate-700 lg:hidden"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 text-xl font-extrabold text-teal-800">
            <img src="/pep-vida/brand/logo-vida.png" alt="Vida Diagnóstico e Saúde" className="h-12 w-auto sm:h-14" />
          </div>
          <div className="flex items-center gap-3 rounded-2xl border bg-slate-50 p-2 pr-4">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-teal-700 to-sky-900 font-extrabold text-white">
              {getInitials(paciente.nome)}
            </div>
            <div className="hidden sm:block">
              <div className="text-sm font-extrabold">{paciente.nome ?? `Paciente ${paciente.cdPaciente}`}</div>
              <div className="text-xs text-slate-500">Paciente</div>
            </div>
          </div>
          <button className="hidden rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50 sm:block" onClick={sair}>
            Sair
          </button>
        </div>
      </header>

      <section className="grid min-h-[calc(100vh-78px)] lg:grid-cols-[270px_1fr]">
        {mobileMenuOpen && <div className="fixed inset-0 z-30 bg-slate-950/45 lg:hidden" onClick={() => setMobileMenuOpen(false)} />}
        <aside className={`fixed inset-y-0 left-0 z-40 w-[270px] overflow-y-auto bg-[#103f55] p-5 text-slate-100 transition-transform lg:static lg:block lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="mb-4 flex items-center justify-between lg:hidden">
            <span className="text-sm font-extrabold">Menu</span>
            <button className="grid h-10 w-10 place-items-center rounded-xl border border-white/15" onClick={() => setMobileMenuOpen(false)} aria-label="Fechar menu">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="px-3 text-[11px] font-extrabold uppercase tracking-[.14em] text-sky-200">Navegação</p>
          <div className="mt-3 grid gap-2">
            <button
              className={`rounded-xl px-4 py-3 text-left font-bold ${activeView === 'all' ? 'bg-white text-[#174b67]' : 'text-slate-200'}`}
              onClick={() => {
                setActiveView('all');
                setMobileMenuOpen(false);
                scrollToSection('topo-resultados');
              }}
            >
              Meus resultados
            </button>
            <button
              className={`rounded-xl px-4 py-3 text-left font-bold ${activeView === 'checkup' ? 'bg-white text-[#174b67]' : 'text-slate-200'}`}
              onClick={() => {
                setActiveView('checkup');
                setOpenCheckupMenu((value) => !value);
                scrollToSection('meu-checkup');
              }}
            >
              Meu Checkup
            </button>
            {openCheckupMenu && (
              <div className="grid gap-1 pl-3">
                <YearButton label="Todos os anos" active={selectedCheckupYear === 'todos'} onClick={() => { setSelectedCheckupYear('todos'); setMobileMenuOpen(false); }} />
                {checkupYears.map((year) => (
                  <YearButton key={year} label={year} active={selectedCheckupYear === year} onClick={() => { setSelectedCheckupYear(year); setMobileMenuOpen(false); }} />
                ))}
              </div>
            )}
            <button
              className={`rounded-xl px-4 py-3 text-left font-bold ${activeView === 'outros' ? 'bg-white text-[#174b67]' : 'text-slate-200'}`}
              onClick={() => {
                setActiveView('outros');
                setOpenOutrosMenu((value) => !value);
                scrollToSection('outros-resultados');
              }}
            >
              Outros resultados
            </button>
            {openOutrosMenu && (
              <div className="grid gap-1 pl-3">
                <YearButton label="Todos os anos" active={selectedOutrosYear === 'todos'} onClick={() => { setSelectedOutrosYear('todos'); setMobileMenuOpen(false); }} />
                {outrosYears.map((year) => (
                  <YearButton key={year} label={year} active={selectedOutrosYear === year} onClick={() => { setSelectedOutrosYear(year); setMobileMenuOpen(false); }} />
                ))}
              </div>
            )}
            <button className="mt-2 rounded-xl border border-white/15 px-4 py-3 text-left font-bold text-slate-200" onClick={sair}>
              Sair
            </button>
          </div>
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/10 p-5">
            <strong className="text-white">Central de atendimento</strong>
            <p className="mt-2 text-sm leading-6 text-sky-100/80">Em caso de dúvida, fale com a unidade onde realizou o exame.</p>
          </div>
        </aside>

        <section className="min-w-0 p-5 lg:p-8">
          <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[.16em] text-teal-700">Portal do paciente</p>
              <h1 className="mt-1 text-3xl font-black tracking-tight lg:text-4xl">Olá, {firstName(paciente.nome)}</h1>
              <p className="mt-1 text-slate-500">Acompanhe seus laudos e imagens disponíveis.</p>
            </div>
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-green-200 bg-green-50 px-4 py-2 text-sm font-extrabold text-green-700">
              <ShieldCheck className="h-4 w-4" />
              Acesso seguro
            </span>
          </div>

          <div id="topo-resultados" className="scroll-mt-24 rounded-[24px] bg-gradient-to-br from-teal-700 via-teal-600 to-sky-900 p-7 text-white shadow-[0_18px_45px_rgba(15,87,102,.18)]">
            <p className="text-xs font-extrabold uppercase tracking-[.14em] text-teal-100">Resultados disponíveis</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight">{exames.length} registros encontrados</h2>
            <p className="mt-2 max-w-2xl text-white/75">Laudos assinados e exames com imagem ficam disponíveis conforme liberação clínica.</p>
          </div>

          {(activeView === 'all' || activeView === 'checkup') && checkupExames.length > 0 && (
            <section id="meu-checkup" className="mt-5 scroll-mt-24 rounded-2xl border border-teal-100 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[.14em] text-teal-700">Programa Vida</p>
                  <h2 className="text-xl font-black">Meu Checkup</h2>
                </div>
                <span className="w-fit rounded-full bg-teal-50 px-3 py-1 text-sm font-bold text-teal-700">
                  {selectedCheckupYear === 'todos' ? 'Todos os anos' : selectedCheckupYear} · {visibleCheckupExames.length} registros
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <SectionButton label="Relatórios" count={checkupRelatorios.length} active={checkupSection === 'relatorios'} onClick={() => setCheckupSection('relatorios')} />
                <SectionButton label="Receitas" count={checkupReceitas.length} active={checkupSection === 'receitas'} onClick={() => setCheckupSection('receitas')} />
                <SectionButton label="Plano Alimentar" count={checkupPlano.length} active={checkupSection === 'plano'} onClick={() => setCheckupSection('plano')} />
                <SectionButton label="Exames" count={checkupExamesLista.length} active={checkupSection === 'exames'} onClick={() => setCheckupSection('exames')} />
                <SectionButton label="Encaminhamentos" count={checkupEncaminhamentos.length} active={checkupSection === 'encaminhamentos'} onClick={() => setCheckupSection('encaminhamentos')} />
              </div>
              {visibleCheckupExames.length === 0 ? (
                <EmptyState text="Nenhum registro nesta sessão para o ano selecionado." />
              ) : (
                <ResultTimeline exames={visibleCheckupExames} onSelect={setSelected} />
              )}
            </section>
          )}

          {checkupExames.length === 0 && (
            <section className="mt-5 overflow-hidden rounded-2xl border border-teal-100 bg-gradient-to-br from-teal-50 via-white to-cyan-50 p-6 shadow-sm sm:p-8">
              <p className="text-xs font-extrabold uppercase tracking-[.16em] text-teal-700">Programa Vida</p>
              <h2 className="mt-2 max-w-3xl text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                Você ainda não fez o Check-Up Vida conosco?
              </h2>
              <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600">
                O Check-Up Vida reúne, em um só dia e lugar, atendimento médico multidisciplinar e os exames indicados para sua idade, sexo e necessidades de saúde. O programa integra avaliações clínicas, nutrição e psicologia, com retorno em até 15 dias — presencial ou à distância, conforme sua preferência.
              </p>
              <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600">
                Mais do que realizar exames, o Check-Up Vida ajuda a identificar doenças em fases iniciais e possíveis fatores de risco, promovendo uma medicina preventiva, ética e personalizada para cuidar hoje da sua saúde e da sua qualidade de vida no futuro.
              </p>
              <button type="button" onClick={demonstrarInteresseCheckup} disabled={interestLoading || interestSent} className="mt-6 rounded-xl bg-teal-700 px-5 py-3 font-extrabold text-white shadow-sm transition hover:bg-teal-800 disabled:cursor-default disabled:opacity-80">
                {interestSent ? 'Interesse registrado' : interestLoading ? 'Registrando...' : 'Tenho interesse no Check-Up Vida'}
              </button>
              {interestSent && <p className="mt-3 text-sm font-semibold text-teal-800">Obrigado pelo interesse. Um de nossos consultores entrará em contato para fornecer mais informações.</p>}
            </section>
          )}

          {(activeView === 'all' || activeView === 'outros') && (
          <section id="outros-resultados" className="mt-5 scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-xl font-black">{checkupExames.length > 0 ? 'Outros resultados' : 'Laudos disponíveis'}</h2>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-bold text-slate-600">
                {selectedOutrosYear === 'todos' ? 'Todos os anos' : selectedOutrosYear} · {filteredOutrosExames.length} registros
              </span>
            </div>

            {exames.length === 0 && <EmptyState text="Nenhum laudo disponível para este acesso." />}
            {outrosExames.length === 0 && exames.length > 0 && <EmptyState text="Todos os resultados deste acesso pertencem ao Meu Checkup." />}
            <ResultTimeline exames={filteredOutrosExames} onSelect={setSelected} />
          </section>
          )}
        </section>
      </section>

      {selected && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b px-5 py-4">
              <div>
                <h3 className="font-black">{selected.exame.procedimento ?? 'Laudo'}</h3>
                <p className="text-sm text-slate-500">{selected.exame.data ?? 'Sem data'}</p>
              </div>
              <div className="flex gap-2">
                {selected.tipo === 'laudo' && (
                  <a className="inline-flex items-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-bold text-teal-800 hover:bg-teal-100" href={pdfUrl} download={`laudo-${selected.exame.cdExame}.pdf`}>
                    <Download className="h-4 w-4" />
                    Baixar PDF
                  </a>
                )}
                {selected.tipo === 'dicom' && (
                  <a className="rounded-xl border px-4 py-2 text-sm font-bold" href={dicomWebUrl} target="_blank" rel="noreferrer">
                    Abrir em nova aba
                  </a>
                )}
                <button className="rounded-xl border px-4 py-2 text-sm font-bold" onClick={() => setSelected(null)}>
                  Fechar
                </button>
              </div>
            </div>
            {selected.tipo === 'laudo' ? (
              <iframe title="Laudo PDF" src={pdfUrl} className="h-[76vh] w-full bg-white" />
            ) : selected.tipo === 'dicom' ? (
              <div className="relative h-[76vh] w-full bg-black">
                {dicomFrameUrl ? (
                  <iframe title="DICOM web" src={dicomFrameUrl} className="h-full w-full bg-black" />
                ) : (
                  <div className="grid h-full place-items-center text-center text-white">
                    <div>
                      <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin" />
                      <p>{dicomError || (dicomLoading ? 'Carregando estudo DICOM...' : 'Preparando estudo DICOM...')}</p>
                      {dicomError && (
                        <a className="mt-4 inline-flex rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-950" href={dicomWebUrl} target="_blank" rel="noreferrer">
                          Abrir em nova aba
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="max-h-[76vh] overflow-auto bg-slate-100 p-3">
                {(selected.exame.imagemPdfs ?? []).map((cdLaudo, index) => (
                  <iframe
                    key={cdLaudo}
                    title={`Imagens ${index + 1}`}
                    src={`${apiUrl}/api/portal-resultados/${selected.exame.cdExame}/imagens/${cdLaudo}.pdf`}
                    className="mb-4 h-[76vh] w-full rounded-xl bg-white"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

function EmptyState({ text }: { text: string }) {
  return <div className="mt-4 rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">{text}</div>;
}

function Field({ label, icon, children }: { label: string; icon: ReactNode; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-bold">{label}</span>
      <span className="relative block">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">{icon}</span>
        {children}
      </span>
    </label>
  );
}

function ResultTimeline({
  exames,
  onSelect,
}: {
  exames: PortalExame[];
  onSelect: (value: { exame: PortalExame; tipo: 'laudo' | 'imagens' | 'dicom' }) => void;
}) {
  return (
    <div className="mt-5 grid gap-3">
      {exames.map((exame, index) => (
        <article key={`${exame.cdExame}-${exame.cdAtendimento}`} className="grid grid-cols-[26px_1fr] gap-3">
          <div className="flex flex-col items-center">
            <span className="mt-5 h-3.5 w-3.5 rounded-full bg-teal-700 shadow-[0_0_0_5px_#e9f7f5]" />
            {index < exames.length - 1 && <span className="mt-3 w-px flex-1 bg-slate-200" />}
          </div>
          <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-teal-200 hover:shadow-md sm:grid-cols-[1fr_auto] sm:items-center">
            <div className="min-w-0">
              <div className="flex items-start gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-slate-100 text-teal-700">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-extrabold">{exame.procedimento ?? 'Exame'}</h3>
                    {exame.isCheckup && <span className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-extrabold text-teal-700">CHECKUP</span>}
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    {exame.data ?? 'Sem data'}
                    {exame.nrControle ? ` · Controle ${exame.nrControle}` : ''}
                    {exame.modalidade ? ` · ${exame.modalidade}` : ''}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:justify-end">
              {exame.temLaudo && (
                <button className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold hover:bg-teal-50" onClick={() => onSelect({ exame, tipo: 'laudo' })}>
                  <Eye className="h-4 w-4" />
                  Laudo
                </button>
              )}
              {!exame.temLaudo && (
                <span className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700" title="O laudo ainda não foi liberado">
                  <Clock3 className="h-4 w-4" />
                  Aguardando laudo
                </span>
              )}
              {exame.temImagem && (
                exame.isCapturaPdf ? (
                  <button className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold hover:bg-teal-50" onClick={() => onSelect({ exame, tipo: 'imagens' })}>
                    <ImageIcon className="h-4 w-4" />
                    Imagens
                  </button>
                ) : exame.pacsUrl ? (
                  <>
                    <button
                      className="inline-flex items-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-bold text-teal-800 hover:bg-teal-100"
                      onClick={() => onSelect({ exame, tipo: 'dicom' })}
                    >
                      <ExternalLink className="h-4 w-4" />
                      DICOM web
                    </button>
                    <a
                      className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold hover:bg-teal-50"
                      href={exame.pacsUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ImageIcon className="h-4 w-4" />
                      Imagem
                    </a>
                  </>
                ) : null
              )}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function YearButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      className={`rounded-lg px-4 py-2 text-left text-sm font-bold ${
        active ? 'bg-teal-100 text-teal-950' : 'text-sky-100 hover:bg-white/10'
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function SectionButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`rounded-xl px-4 py-2 text-sm font-extrabold ${
        active ? 'bg-teal-700 text-white shadow-sm' : 'border border-slate-200 bg-white text-slate-700 hover:bg-teal-50'
      }`}
      onClick={onClick}
    >
      {label} · {count}
    </button>
  );
}

function getInitials(name?: string | null) {
  return (name ?? 'Paciente')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function firstName(name?: string | null) {
  return name?.split(' ').filter(Boolean)[0] ?? 'paciente';
}

function getYear(date?: string | null) {
  const match = date?.match(/\d{2}\/\d{2}\/(\d{4})/);
  return match?.[1] ?? '';
}

function getYears(exames: PortalExame[]) {
  return Array.from(new Set(exames.map((exame) => getYear(exame.data)).filter(Boolean))).sort((a, b) => Number(b) - Number(a));
}

function sortByRecentDate(exames: PortalExame[]) {
  return [...exames].sort((a, b) => dateValue(b.data) - dateValue(a.data));
}

function dateValue(date?: string | null) {
  const match = date?.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (!match) return 0;
  const [, day, month, year] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
}

function normalizeText(value?: string | null) {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

function isReceita(exame: PortalExame) {
  const text = normalizeText(exame.procedimento);
  return text.includes('RECEITA') || text.includes('PRESCRICAO');
}

function isRelatorio(exame: PortalExame) {
  return normalizeText(exame.procedimento).includes('CONSULTA');
}

function isPlanoAlimentar(exame: PortalExame) {
  return normalizeText(exame.procedimento).includes('PLANO ALIMENTAR');
}

function isEncaminhamento(exame: PortalExame) {
  return normalizeText(exame.procedimento).includes('ENCAMINHAMENTO');
}

function isRetornoCheckup(exame: PortalExame) {
  const text = normalizeText(exame.procedimento);
  return text.includes('RETORNO') && text.includes('CHECKUP');
}

function isExame(exame: PortalExame) {
  return !isRelatorio(exame) && !isReceita(exame) && !isPlanoAlimentar(exame) && !isEncaminhamento(exame);
}

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

async function portalFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${apiUrl}/api/portal-resultados${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || 'Falha ao acessar resultados');
  }
  return res.json();
}

function getInitialCredentials() {
  const params = new URLSearchParams(window.location.search);
  const parts = window.location.pathname.split('/').filter(Boolean);
  // So usa path/query como credencial quando a URL for a rota dinamica
  // (/pep-vida/resultado/<protocolo>/<cdPaciente>, 4+ segmentos) ou
  // quando houver query params explicitos. Caso contrario (rota raiz
  // /pep-vida/resultado), retorna vazio para nao preencher os campos
  // com os literais do path estatico.
  const temQueryParam = params.get('protocolo') || params.get('paciente');
  const emRotaDinamica = parts.length >= 4;
  if (!temQueryParam && !emRotaDinamica) {
    return { protocolo: '', cdPaciente: '' };
  }
  return {
    protocolo: params.get('protocolo') ?? parts.at(-2) ?? '',
    cdPaciente: params.get('paciente') ?? parts.at(-1) ?? '',
  };
}
